import React, { useCallback } from 'react'
import { Box, Tooltip } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { StyledIconButton, StyledIcon } from '../styles'
import { useModal } from '@app/helpers'
import { faHighlighter } from '@fortawesome/pro-solid-svg-icons'
import { useSnapshot } from 'valtio'
import { CurrentTestState } from '@app/storage'
import { useHighlightMode } from '../hooks'
import ToolsMenuItem from './ToolsMenuItem'
import { DraggableHighlight } from './DraggableHighlight'

// a11y: entry-point hint so keyboard users discover the shortcut (referenced by the Highlight toggle)
const HIGHLIGHT_SHORTCUT_HINT_ID = 'highlight-shortcut-hint'

export type Props = {
  isReviewMode: boolean
  isMobile?: boolean
}

const Highlight: React.FC<Props> = ({ isReviewMode, isMobile = false }) => {
  const { open, onOpen, onClose } = useModal(false)
  const { currentItemResponse } = useSnapshot(CurrentTestState)

  const { reset, ...highlightProps } = useHighlightMode({
    itemId: currentItemResponse?.itemId,
    passageId: currentItemResponse?.passageId,
    // a11y: highlighting is disabled in review mode — gate the global Ctrl+Shift+H listener (the toolbar
    // already doesn't render, so this closes the keyboard path that otherwise stayed active in review).
    enabled: !isReviewMode,
  })

  const handleClose = useCallback(() => {
    reset()
    onClose()
  }, [onClose, reset])

  if (isReviewMode) return null

  return (
    <>
      {isMobile ? (
        <ToolsMenuItem
          icon={faHighlighter}
          label='Highlight'
          checked={open}
          onChange={open ? onClose : onOpen}
        />
      ) : (
        <Tooltip title='Highlight (Ctrl+Shift+H)'>
          <StyledIconButton
            aria-label='Highlight'
            aria-describedby={HIGHLIGHT_SHORTCUT_HINT_ID}
            aria-pressed={open}
            onClick={open ? handleClose : onOpen}
            className={open ? 'active' : ''}
          >
            <StyledIcon icon={faHighlighter} />
          </StyledIconButton>
        </Tooltip>
      )}

      {open && (
        <DraggableHighlight
          isReviewMode={isReviewMode}
          isMobile={isMobile}
          onClose={handleClose}
          highlight={highlightProps}
        />
      )}

      {/* a11y: entry-point hint so keyboard users discover the highlight shortcut */}
      <Box component='span' id={HIGHLIGHT_SHORTCUT_HINT_ID} sx={visuallyHidden}>
        Select text, then press Control plus Shift plus H to highlight it.
      </Box>
    </>
  )
}

export default Highlight
